/**
 * MSSQL instance-version → SQL Server *year* mapping (INT-1).
 *
 * IntelliSense version gating compares the connection's server version against
 * the `versionMetadata` `addedIn` tokens, which for MSSQL are **year format**
 * (`'2012' | '2016' | '2017' | '2019' | '2022'` — see versionMetadata.ts).
 *
 * `SERVERPROPERTY('ProductVersion')` returns the *internal build* string
 * (e.g. `"16.0.1000.6"`, `"14.0.3456.2"`) which has **no year**, so comparing
 * it to a year token via `compareVersions` always yields a hugely negative
 * number (`16 - 2022 = -2006`) → every year-gated feature is falsely badged
 * `'unavailable'`. We instead probe `SERVERPROPERTY('ProductMajorVersion')`
 * (an integer major: 11/12/13/14/15/16/17…, available on SQL Server 2012+)
 * and map the major to the marketing year here.
 *
 * Mapping (Microsoft "SQL Server major version" → marketing year):
 *   10 → '2008'   (ProductMajorVersion absent pre-2012; fallback path only)
 *   11 → '2012'
 *   12 → '2014'
 *   13 → '2016'
 *   14 → '2017'
 *   15 → '2019'
 *   16 → '2022'
 *   17 → '2025'
 *
 * Unknown-major fail-safe policy: an unrecognized **future** major (e.g. 18+)
 * MUST NOT cause `classifyFeature` to badge known year-gated features as
 * `'unavailable'`. Returning the bare major (e.g. `'18'`) would do exactly
 * that (`compareVersions('18', '2022') < 0`). So for an unknown major we
 * return `undefined` → the projector maps that to "no serverVersion" →
 * gating stays **inert** (every feature treated as supported). That is the
 * correct fail-safe: a newer-than-known server certainly supports every
 * feature we know about, so never strike them through.
 */

const MAJOR_TO_YEAR: Readonly<Record<number, string>> = {
  10: '2008',
  11: '2012',
  12: '2014',
  13: '2016',
  14: '2017',
  15: '2019',
  16: '2022',
  17: '2025',
};

/** The lowest known major; anything below this is treated as "too old to gate". */
const MIN_KNOWN_MAJOR = 10;
/** The highest known major; anything above is a future server (fail-safe). */
const MAX_KNOWN_MAJOR = 17;

/**
 * Map a SQL Server `ProductMajorVersion` integer to the marketing year string
 * that `versionMetadata.addedIn` uses for MSSQL.
 *
 * - Known major (10–17) → its marketing year (`'2008'`…`'2025'`).
 * - Unknown **future** major (>17) → `undefined` (fail-safe: gating stays
 *   inert so we never falsely strike a feature on a newer server).
 * - Unknown **older** major (<10) → `undefined` (we have no year-gated
 *   features below 2008; treating as unknown keeps gating inert rather than
 *   guessing).
 * - Non-finite / non-positive input → `undefined`.
 */
export function mssqlMajorVersionToYear(major: number): string | undefined {
  if (!Number.isFinite(major) || major <= 0) return undefined;
  const m = Math.trunc(major);
  if (m < MIN_KNOWN_MAJOR || m > MAX_KNOWN_MAJOR) return undefined;
  return MAJOR_TO_YEAR[m];
}

/**
 * Coerce the raw `ProductMajorVersion` cell (number or numeric string) into a
 * year, with the fallback to parsing the leading integer of a full
 * `ProductVersion` build string (e.g. `"14.0.3456.2"` → 14). Returns `''`
 * when nothing usable is present so the cache/ projector keep their existing
 * "empty → undefined → inert gating" contract.
 */
export function resolveMssqlServerYear(
  productMajorVersion: unknown,
  productVersionFallback?: unknown,
): string {
  const major = coerceLeadingInt(productMajorVersion) ?? coerceLeadingInt(productVersionFallback);
  if (major === undefined) return '';
  return mssqlMajorVersionToYear(major) ?? '';
}

function coerceLeadingInt(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.trunc(value);
  if (typeof value === 'string') {
    const m = value.trim().match(/^\d+/);
    if (m) return Number.parseInt(m[0], 10);
  }
  return undefined;
}
